import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledNavigationBar = styled.div<{ $hasBackButton: boolean }>`
  background-color: var(--beige-main);
  border: 1px solid var(--gray-dark);
  border-top: none;
  border-radius: 0 0 var(--xsmall-border-radius) var(--xsmall-border-radius);
  padding: 25px;
  display: flex;
  align-items: center;
  justify-content: ${({ $hasBackButton }) =>
    $hasBackButton ? 'space-between' : 'flex-start'};
  gap: var(--default-padding);

  @media screen and (max-width: 480px) {
    padding: 15px;
  }
`;

const StyledNavButtonRight = styled.div`
  margin-left: auto;
`;

const StyledNavButton = styled.button<{ $primary?: boolean }>`
  all: unset;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 14px 24px;
  border-radius: var(--default-border-radius);
  font-size: var(--small-font-size);
  line-height: 21px;
  font-weight: 500;
  transition: opacity 0.2s ease;

  ${({ $primary }) =>
    $primary
      ? `
    background-color: var(--red-dark);
    color: var(--white-main);
  `
      : `
    background-color: transparent;
    color: var(--purple-dark);
    border: 1px solid var(--purple-dark);
  `}

  &:hover {
    opacity: 0.9;
  }

  &:focus-visible {
    outline: 2px solid var(--red-dark);
    outline-offset: 2px;
  }

  @media screen and (max-width: 480px) {
    padding: var(--xsmall-padding) var(--medium-padding);
    font-size: var(--xsmall-font-size);
  }
`;

const StyledStepCounter = styled.span`
  font-size: var(--small-font-size);
  line-height: 21px;
  font-weight: 500;
  color: var(--black-main);

  @media screen and (max-width: 480px) {
    font-size: var(--xsmall-font-size);
  }
`;

type SimulationDetailNavigationProps = {
  activeStep: number;
  totalSteps: number;
  onBack: () => void;
  onNext: () => void;
  onFinish: () => void;
};

export const SimulationDetailNavigation: React.FC<
  SimulationDetailNavigationProps
> = ({ activeStep, totalSteps, onBack, onNext, onFinish }) => {
  const { t } = useTranslation();
  const hasBackButton = activeStep > 0;
  const isLastStep = activeStep === totalSteps - 1;

  return (
    <StyledNavigationBar $hasBackButton={hasBackButton}>
      {hasBackButton && (
        <StyledNavButton onClick={onBack}>
          {t('phishing.followUp.detail.navigation.back')}
        </StyledNavButton>
      )}
      <StyledStepCounter>
        {t('phishing.followUp.detail.step.sectionOf', {
          current: activeStep + 1,
          total: totalSteps,
        })}
      </StyledStepCounter>
      {hasBackButton ? (
        isLastStep ? (
          <StyledNavButton $primary onClick={onFinish}>
            {t('phishing.followUp.detail.navigation.finish')}
          </StyledNavButton>
        ) : (
          <StyledNavButton $primary onClick={onNext}>
            {t('phishing.followUp.detail.navigation.next')}
          </StyledNavButton>
        )
      ) : (
        <StyledNavButtonRight>
          <StyledNavButton $primary onClick={onNext}>
            {t('phishing.followUp.detail.navigation.next')}
          </StyledNavButton>
        </StyledNavButtonRight>
      )}
    </StyledNavigationBar>
  );
};
